import { useSetCampaignId } from '@/entities/campaign/model';
import { useCampaignStore, VALID_CAMPAIGNS } from '@/entities/campaign/model/campaignStore';
import { useCheckCampaignAchieved } from '@/entities/campaign/model/useCheckCampaignAchieved';
import { AlbumOrientation } from '@/features/AlbumOrientation';
import { useAppStore } from '@/shared/lib/store/appStore';
import TournamentCampaignPopup from '@/widgets/CampaignPopups';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import AppRouter from '../router';
const log = console.log.bind(console, '[APP CONTENT]:');

const AppContent: React.FC = () => {
	useSetCampaignId();
	useCheckCampaignAchieved();

	const {
		campaignId,
		isCampaignGoalAchieved,
		campaignModalClosedTime,
		completedOffersCount,
		lastCompletedOfferEndTime,
		countCampaignPopupTimesClosed,
		isReachedMainPage,
		setCountCampaignPopupTimesClosed,
		setCompletedOffersCount,
		setIsOngoingTimeout,
	} = useCampaignStore();

	const { appStatus, gameOverReason } = useAppStore();
	const [campaignClosedTimeElapsed, setCampaignClosedTimeElapsed] = useState<number>(0);

	const campaignTimeoutDuration = useMemo(() => (campaignId === VALID_CAMPAIGNS[0] ? 120_000 : 30_000), [campaignId]);

	useEffect(() => {
		log('campaignModalClosedTime has changed');

		if (isCampaignGoalAchieved) {
			log('campaign goal achieved. return');

			return;
		}

		if (!campaignModalClosedTime || campaignId !== VALID_CAMPAIGNS[0]) {
			log('campaignModalClosedTime has changed. But something is off');

			return;
		}

		log('Campaign Id is ', VALID_CAMPAIGNS[0]);
		log('Starting timeout to show modal after', campaignTimeoutDuration);

		const timeout = setTimeout(() => {
			log("Time's up showing modal");
			setCampaignClosedTimeElapsed(Date.now() - campaignModalClosedTime);
			setIsOngoingTimeout(false);
		}, campaignTimeoutDuration);

		setIsOngoingTimeout(true);

		return () => {
			log('Clearing old timeout and resetting ongoing timeout flag');
			clearTimeout(timeout);
			setIsOngoingTimeout(false);
		};
	}, [campaignId, campaignModalClosedTime, campaignTimeoutDuration, isCampaignGoalAchieved]);

	useEffect(() => {
		if (isCampaignGoalAchieved) {
			log('campaign goal achieved. return');

			return;
		}

		if (!lastCompletedOfferEndTime || campaignId !== VALID_CAMPAIGNS[1]) return;

		log('Campaign Id is ', VALID_CAMPAIGNS[1]);
		log('Starting timeout to show modal after' + campaignTimeoutDuration);

		const timeout = setTimeout(() => {
			log("Time's up showing modal");
			setCampaignClosedTimeElapsed(Date.now() - lastCompletedOfferEndTime);
			setIsOngoingTimeout(false);
		}, campaignTimeoutDuration);

		setIsOngoingTimeout(true);

		return () => {
			log('Clearing old timeout and resetting ongoing timeout flag');
			clearTimeout(timeout);
			setIsOngoingTimeout(false);
		};
	}, [campaignId, lastCompletedOfferEndTime, campaignTimeoutDuration, isCampaignGoalAchieved]);

	const shouldShowPopup = useMemo(() => {
		return (
			campaignId != null &&
			!isCampaignGoalAchieved &&
			appStatus === 'idle' &&
			gameOverReason === null &&
			isReachedMainPage &&
			countCampaignPopupTimesClosed < 2
		);
		// eslint-disable-next-line max-len
	}, [campaignId, isCampaignGoalAchieved, appStatus, gameOverReason, isReachedMainPage, countCampaignPopupTimesClosed]);

	const campaignRenderCondition = useMemo(() => {
		if (campaignId === VALID_CAMPAIGNS[0]) {
			const secondPart =
				// eslint-disable-next-line max-len
				!campaignModalClosedTime || campaignClosedTimeElapsed >= campaignTimeoutDuration || completedOffersCount > 1;

			return shouldShowPopup && secondPart;
		} else if (campaignId === VALID_CAMPAIGNS[1]) {
			const secondPart = campaignClosedTimeElapsed >= campaignTimeoutDuration && completedOffersCount !== 0;

			return shouldShowPopup && secondPart;
		}

		return false;
	}, [
		campaignId,
		shouldShowPopup,
		campaignModalClosedTime,
		campaignClosedTimeElapsed,
		campaignTimeoutDuration,
		completedOffersCount,
	]);

	return (
		<>
			<AnimatePresence mode='wait'>
				{campaignRenderCondition && (
					<TournamentCampaignPopup
						key={`tournament-campaign-popup-${campaignId}-${countCampaignPopupTimesClosed}`}
						// отключаем линтер т.к внутри campaignRenderCondition есть условие shouldShowPopup, которое в себе прописывает проверку на campaignId != null
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						campaignId={campaignId!}
						onCloseClick={() => {
							setCompletedOffersCount(0);
							setCampaignClosedTimeElapsed(0);
							setCountCampaignPopupTimesClosed(countCampaignPopupTimesClosed + 1);
						}}
						onJoinClick={() => {
							setCampaignClosedTimeElapsed(0);
							setCountCampaignPopupTimesClosed(countCampaignPopupTimesClosed + 1);
						}}
					/>
				)}
				<AlbumOrientation key='album-orientation-checker' />
			</AnimatePresence>
			<AppRouter />
		</>
	);
};

export default AppContent;
